import React, { useEffect, useState } from "react";
import Modal from "../../components/Modal";
import {
  getClaimTemplates,
  createClaimTemplate,
  updateClaimTemplate,
  deleteClaimTemplate,
} from "../../apis/insuranceProviders";
import { getServiceComponents } from "../../apis/serviceComponents";

interface Props {
  open: boolean;
  onClose: () => void;
  providerId: number;
  providerName: string;
}

const emptyTemplate = {
  providerLabel: "",
  billingSourceType: "component" as "component" | "pharmacy" | "manual",
  serviceComponentId: "" as string | number,
  displayOrder: 0,
  isVisible: true,
};

const SOURCE_BADGE: Record<string, { label: string; cls: string }> = {
  component: { label: "Component", cls: "bg-blue-100 text-blue-800" },
  pharmacy:  { label: "Pharmacy",  cls: "bg-green-100 text-green-800" },
  manual:    { label: "Manual",    cls: "bg-gray-100 text-gray-700" },
};

const ClaimTemplatesModal: React.FC<Props> = ({ open, onClose, providerId, providerName }) => {
  const [templates, setTemplates] = useState<any[]>([]);
  const [components, setComponents] = useState<any[]>([]);
  const [form, setForm] = useState({ ...emptyTemplate });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchTemplates();
    fetchComponents();
    resetForm();
  }, [open, providerId]);

  const fetchTemplates = async () => {
    try {
      const res: any = await getClaimTemplates(providerId);
      setTemplates(res.data ?? res);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchComponents = async () => {
    try {
      const res: any = await getServiceComponents({ per_page: 200 });
      const d = res?.data ?? res;
      setComponents(Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : []);
    } catch (e) {
      console.error(e);
    }
  };

  const resetForm = () => {
    setForm({ ...emptyTemplate });
    setEditingId(null);
    setErrors({});
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.providerLabel.trim()) e.providerLabel = "Required";
    if (form.billingSourceType === "component" && !form.serviceComponentId)
      e.serviceComponentId = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        providerLabel: form.providerLabel,
        billingSourceType: form.billingSourceType,
        serviceComponentId:
          form.billingSourceType === "component" ? form.serviceComponentId || undefined : undefined,
        displayOrder: form.displayOrder,
        isVisible: form.isVisible,
      };
      if (editingId) {
        await updateClaimTemplate(providerId, editingId, payload);
      } else {
        await createClaimTemplate(providerId, payload);
      }
      await fetchTemplates();
      resetForm();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (t: any) => {
    setEditingId(t.id);
    setForm({
      providerLabel: t.providerLabel ?? t.provider_label ?? "",
      billingSourceType: (t.billingSourceType ?? t.billing_source_type ?? "component") as any,
      serviceComponentId: t.serviceComponentId ?? t.service_component_id ?? "",
      displayOrder: t.displayOrder ?? t.display_order ?? 0,
      isVisible: t.isVisible ?? t.is_visible ?? true,
    });
    setErrors({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (templateId: number) => {
    if (!window.confirm("Delete this claim template mapping?")) return;
    try {
      await deleteClaimTemplate(providerId, templateId);
      fetchTemplates();
    } catch (e) {
      console.error(e);
    }
  };

  if (!open) return null;

  const inputCls =
    "mt-1 block w-full rounded-md border border-gray-300 shadow-sm text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent";

  return (
    <Modal
      title={`Claim Templates — ${providerName}`}
      onClose={onClose}
      width="sm:max-w-5xl"
      modalContent={
        <div className="space-y-6">
          {/* ── Add / edit form ── */}
          <div
            className={`rounded-lg border p-5 ${
              editingId ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200"
            }`}
          >
            <h3 className="text-sm font-semibold text-gray-800 mb-4">
              {editingId ? "✏️ Edit Mapping" : "Add Mapping"}
            </h3>

            {/* Row 1: Label + Source + Component */}
            <div className="grid grid-cols-12 gap-4">
              {/* Provider label — 4 cols */}
              <div className="col-span-12 sm:col-span-4">
                <label className="block text-xs font-medium text-gray-600">
                  Provider Label
                  <span className="ml-1 font-normal text-gray-400">
                    (what the insurer calls it)
                  </span>
                </label>
                <input
                  type="text"
                  value={form.providerLabel}
                  onChange={(e) => setForm({ ...form, providerLabel: e.target.value })}
                  placeholder="e.g. Professional Charges"
                  className={inputCls}
                />
                {errors.providerLabel && (
                  <p className="mt-0.5 text-xs text-red-500">{errors.providerLabel}</p>
                )}
              </div>

              {/* Billing source — 3 cols */}
              <div className="col-span-12 sm:col-span-3">
                <label className="block text-xs font-medium text-gray-600">Billing Source</label>
                <select
                  value={form.billingSourceType}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      billingSourceType: e.target.value as any,
                      serviceComponentId: "",
                    })
                  }
                  className={inputCls}
                >
                  <option value="component">Component (auto-mapped)</option>
                  <option value="pharmacy">Pharmacy Total (auto)</option>
                  <option value="manual">Manual entry</option>
                </select>
              </div>

              {/* Service component — 5 cols, only when source = component */}
              <div className="col-span-12 sm:col-span-5">
                {form.billingSourceType === "component" ? (
                  <>
                    <label className="block text-xs font-medium text-gray-600">
                      Service Component
                      <span className="ml-1 font-normal text-gray-400">(our grouping)</span>
                    </label>
                    <select
                      value={form.serviceComponentId}
                      onChange={(e) => setForm({ ...form, serviceComponentId: e.target.value })}
                      className={inputCls}
                    >
                      <option value="">Select component…</option>
                      {components.map((c: any) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    {errors.serviceComponentId && (
                      <p className="mt-0.5 text-xs text-red-500">{errors.serviceComponentId}</p>
                    )}
                  </>
                ) : (
                  <div className="h-full flex items-end pb-2">
                    <p className="text-xs text-gray-400 italic">
                      {form.billingSourceType === "pharmacy"
                        ? "Pharmacy total is pulled automatically from pharmacy sales."
                        : "Amount entered manually at claim time."}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Row 2: Order + Visible + Actions */}
            <div className="mt-4 flex flex-wrap items-end gap-4">
              <div className="w-32">
                <label className="block text-xs font-medium text-gray-600">Display Order</label>
                <input
                  type="number"
                  min={0}
                  value={form.displayOrder}
                  onChange={(e) => setForm({ ...form, displayOrder: Number(e.target.value) })}
                  className={inputCls}
                />
              </div>

              <div className="flex items-center gap-2 pb-2">
                <input
                  id="isVisible"
                  type="checkbox"
                  checked={form.isVisible}
                  onChange={(e) => setForm({ ...form, isVisible: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-primary"
                />
                <label htmlFor="isVisible" className="text-sm text-gray-700 cursor-pointer">
                  Visible on claim form
                </label>
              </div>

              <div className="flex gap-2 ml-auto">
                {editingId && (
                  <button
                    onClick={resetForm}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                )}
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-5 py-2 text-sm bg-primary text-white rounded-md hover:opacity-90 disabled:opacity-60"
                >
                  {saving ? "Saving…" : editingId ? "Update" : "Add"}
                </button>
              </div>
            </div>
          </div>

          {/* ── Template list ── */}
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-3">
              Mappings ({templates.length})
            </h3>

            {templates.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">
                No mappings yet — add one above.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide w-8">
                        #
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Provider Label
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Source
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Component
                      </th>
                      <th className="py-3 px-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Visible
                      </th>
                      <th className="py-3 px-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {templates.map((t: any, idx: number) => {
                      const sourceType = t.billingSourceType ?? t.billing_source_type ?? "component";
                      const component = t.serviceComponent ?? t.service_component;
                      const badge = SOURCE_BADGE[sourceType] ?? SOURCE_BADGE.manual;
                      const isEditing = editingId === t.id;
                      return (
                        <tr
                          key={t.id}
                          className={isEditing ? "bg-blue-50" : "hover:bg-gray-50"}
                        >
                          <td className="py-3 px-4 text-gray-400 text-xs">
                            {t.displayOrder ?? t.display_order ?? idx + 1}
                          </td>
                          <td className="py-3 px-4 font-medium text-gray-900 whitespace-nowrap">
                            {t.providerLabel ?? t.provider_label}
                            {!(t.isVisible ?? t.is_visible) && (
                              <span className="ml-2 text-xs text-gray-400">(hidden)</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${badge.cls}`}
                            >
                              {badge.label}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-700">
                            {component?.name ?? component?.display_name ?? (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {(t.isVisible ?? t.is_visible) ? (
                              <span className="text-green-600">✓</span>
                            ) : (
                              <span className="text-gray-400">✗</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => handleEdit(t)}
                                className="px-3 py-1.5 text-xs font-medium bg-primary text-white rounded-md hover:opacity-90 whitespace-nowrap"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(t.id)}
                                className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-md hover:opacity-90 whitespace-nowrap"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      }
    />
  );
};

export default ClaimTemplatesModal;
